import { defaultServiceLanguage, type ServiceLanguage } from "@albertine/i18n";
import { useLongPress } from "@reactuses/core";
import type { ReactNode, TouchEvent } from "react";
import { useEffectEvent, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { cn } from "../../../lib/cn";
import { ChatProvider, useChat } from "../ChatContext";
import { type ChatMessageLabels, defaultChatMessageLabels } from "../labels";
import {
    DesktopHoverButtons,
    type DesktopMessagePanel,
} from "./actions/DesktopActions";
import {
    MessageSelectionProvider,
    SelectedMessageOverlay,
    useMessageSelection,
} from "./actions/Selection";
import { SwipeToReply } from "./actions/SwipeToReply";
import { Articles } from "./articles/Articles";
import { Attachments } from "./attachments/Attachments";
import { MessageBubble } from "./Bubble";
import { ProposalCard } from "./bookings/Proposal";
import {
    hasBubbleContent,
    hasMessageContent,
    MessageContent,
    MessageContentWithoutProposal,
} from "./Content";
import { MessageProvider, useMessage } from "./Context";
import { MessageMetadata } from "./Metadata";
import { MessageReaction } from "./Reaction";
import { MessageRow } from "./Row";
import type { ChatMessageProps } from "./types";
import { useIsTouchDevice } from "./useIsTouchDevice";

const LONG_PRESS_DELAY_MS = 400;
const LONG_PRESS_MOVE_CANCEL_PX = 10;

const HIGHLIGHT_CLASS_NAME = [
    "relative",
    "before:pointer-events-none before:absolute before:content-['']",
    "before:-top-8 before:-right-12 before:-bottom-8 before:-left-12",
    "before:bg-text-primary/10 before:animate-chat-message-highlight",
].join(" ");

export function ChatMessage(message: ChatMessageProps) {
    return (
        <MessageProvider message={message}>
            <MessageLayout />
        </MessageProvider>
    );
}

type ChatMessageWrapperProps = {
    children: ReactNode;
    lang?: ServiceLanguage;
    labels?: ChatMessageLabels;
    onImageLightboxChange?: (open: boolean) => void;
    onOpenImage?: (url: string) => boolean;
};

export function ChatMessageProvider({
    children,
    lang = defaultServiceLanguage,
    labels = defaultChatMessageLabels,
    onImageLightboxChange,
    onOpenImage,
}: ChatMessageWrapperProps) {
    return (
        <ChatProvider
            lang={lang}
            labels={labels}
            onImageLightboxChange={onImageLightboxChange}
            onOpenImage={onOpenImage}
        >
            <MessageSelectionProvider>{children}</MessageSelectionProvider>
        </ChatProvider>
    );
}

export function ChatMessageWrapper({
    children,
    lang = defaultServiceLanguage,
    labels = defaultChatMessageLabels,
    onImageLightboxChange,
    onOpenImage,
}: ChatMessageWrapperProps) {
    return (
        <ChatMessageProvider
            lang={lang}
            labels={labels}
            onImageLightboxChange={onImageLightboxChange}
            onOpenImage={onOpenImage}
        >
            <div
                lang={lang}
                className="overflow-anchor-none box-border flex h-auto w-full flex-col-reverse gap-24 bg-background-chat p-12"
            >
                {children}
            </div>
        </ChatMessageProvider>
    );
}

function MessageLayout() {
    const messageContext = useMessage();
    const {
        id,
        sender,
        proposal,
        message,
        messageHtml,
        deliveryStatus,
        reaction,
        isHighlighted,
        showSenderRow,
        attachments,
    } = messageContext;
    const { lang } = useChat();
    const selection = useMessageSelection();
    const isTouchDevice = useIsTouchDevice();
    const messageRef = useRef<HTMLDivElement>(null);
    const touchStartRef = useRef<{ x: number; y: number } | null>(null);
    const [desktopPanel, setDesktopPanel] = useState<DesktopMessagePanel | null>(null);

    const isSelected = selection.state.selectedMessageId === id;
    const originRect = isSelected ? selection.state.originRect : null;
    const canInteract = deliveryStatus !== "sending" && deliveryStatus !== "failed";
    const canSelect = canInteract && isTouchDevice && !isSelected;
    const canHoverSelect = canInteract && !isTouchDevice && !isSelected;
    const hasMessage = hasMessageContent(message, messageHtml);
    const shouldRenderBubble = hasBubbleContent(messageContext);

    const selectMessage = useEffectEvent((messageId: string, rect: DOMRect) => {
        selection.actions.select(messageId, {
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
        });
    });

    const longPress = useLongPress(
        () => {
            const rect = messageRef.current?.getBoundingClientRect();
            if (!rect) {
                return;
            }

            selectMessage(id, rect);
        },
        {
            delay: LONG_PRESS_DELAY_MS,
            isPreventDefault: false,
        },
    );

    function handleTouchStart(event: TouchEvent<HTMLDivElement>) {
        const touch = event.touches[0];
        if (touch) {
            touchStartRef.current = { x: touch.clientX, y: touch.clientY };
        }
        longPress.onTouchStart(event);
    }

    function handleTouchMove(event: TouchEvent<HTMLDivElement>) {
        const start = touchStartRef.current;
        const touch = event.touches[0];

        if (!start || !touch) {
            return;
        }

        const movedFarEnough =
            Math.abs(touch.clientX - start.x) > LONG_PRESS_MOVE_CANCEL_PX ||
            Math.abs(touch.clientY - start.y) > LONG_PRESS_MOVE_CANCEL_PX;

        if (movedFarEnough) {
            longPress.onTouchEnd();
            touchStartRef.current = null;
        }
    }

    function handleTouchEnd() {
        longPress.onTouchEnd();
        touchStartRef.current = null;
    }

    const messageNode = (
        <div
            id={`chat-message__${id}`}
            lang={lang}
            className={cn(
                "relative w-full",
                sender === "member" ? "self-end" : "self-start",
                reaction && "z-10",
                isHighlighted && HIGHLIGHT_CLASS_NAME,
            )}
        >
            <div
                ref={messageRef}
                className={cn(
                    "relative",
                    isSelected ? "w-full" : "w-fit max-w-[80%]",
                    canHoverSelect && "group/message",
                    canHoverSelect &&
                        cn(
                            sender === "agent"
                                ? reaction
                                    ? "pr-56"
                                    : "pr-40"
                                : reaction
                                  ? "pl-56"
                                  : "pl-40",
                        ),
                    sender === "member" && "ml-auto",
                    isTouchDevice && "select-none [-webkit-touch-callout:none]",
                )}
                onTouchStart={canSelect ? handleTouchStart : undefined}
                onTouchMove={canSelect ? handleTouchMove : undefined}
                onTouchEnd={canSelect ? handleTouchEnd : undefined}
            >
                <SwipeToReply>
                    <div
                        className={cn(
                            "flex flex-col gap-4",
                            sender === "member" ? "items-end" : "items-start",
                        )}
                    >
                        {shouldRenderBubble ? (
                            <>
                                <Attachments />

                                <AgentArticles />

                                <MessageRow>
                                    {proposal && hasMessage ? (
                                        <ProposalMessageBubbles
                                            actions={
                                                canHoverSelect ? (
                                                    <DesktopHoverButtons
                                                        openPanel={desktopPanel}
                                                        onOpenPanel={setDesktopPanel}
                                                        containerRef={messageRef}
                                                    />
                                                ) : null
                                            }
                                        />
                                    ) : (
                                        <div className="relative max-w-full">
                                            <MessageBubble
                                                variant={
                                                    showSenderRow
                                                        ? "default"
                                                        : "standalone"
                                                }
                                            >
                                                <MessageContent />
                                            </MessageBubble>
                                            <MessageReaction />
                                            {canHoverSelect ? (
                                                <DesktopHoverButtons
                                                    openPanel={desktopPanel}
                                                    onOpenPanel={setDesktopPanel}
                                                    containerRef={messageRef}
                                                />
                                            ) : null}
                                        </div>
                                    )}
                                </MessageRow>
                            </>
                        ) : (
                            <div className="relative max-w-full">
                                <Attachments />

                                <AgentArticles />

                                <MessageReaction />
                                {canHoverSelect && attachments.length > 0 ? (
                                    <DesktopHoverButtons
                                        openPanel={desktopPanel}
                                        onOpenPanel={setDesktopPanel}
                                        containerRef={messageRef}
                                    />
                                ) : null}
                            </div>
                        )}

                        <MessageMetadata />
                    </div>
                </SwipeToReply>
            </div>
        </div>
    );

    if (isSelected && originRect) {
        return (
            <>
                <div
                    aria-hidden
                    className={cn(
                        "pointer-events-none mb-4 w-full",
                        sender === "member" ? "self-end" : "self-start",
                    )}
                    style={{ height: originRect.height }}
                />
                {createPortal(
                    <SelectedMessageOverlay
                        originRect={originRect}
                        onDismiss={selection.actions.dismiss}
                    >
                        {messageNode}
                    </SelectedMessageOverlay>,
                    document.body,
                )}
            </>
        );
    }

    return messageNode;
}

function ProposalMessageBubbles({ actions }: { actions?: ReactNode }) {
    const { sender, proposal, showSenderRow } = useMessage();

    if (!proposal) {
        return null;
    }

    return (
        <div
            className={cn(
                "flex w-full flex-col gap-4",
                sender === "member" ? "items-end" : "items-start",
            )}
        >
            <MessageBubble variant="standalone">
                <ProposalCard proposal={proposal} />
            </MessageBubble>

            <div className="relative max-w-full">
                <MessageBubble variant={showSenderRow ? "continuation" : "standalone"}>
                    <MessageContentWithoutProposal />
                </MessageBubble>
                <MessageReaction />
                {actions}
            </div>
        </div>
    );
}

function AgentArticles() {
    const { sender, articles } = useMessage();

    if (sender === "member" || !articles.length) {
        return null;
    }

    return <Articles />;
}
